"use client";

import type { IconProps } from "@phosphor-icons/react";
import {
  CaretUpDownIcon,
  ChatsCircleIcon,
  CrownSimpleIcon,
  EnvelopeIcon,
  FileTextIcon,
  GearIcon,
  HouseIcon,
  InfoIcon,
  NotePencilIcon,
  PaperclipIcon,
  QuestionIcon,
  RocketLaunchIcon,
  ShootingStarIcon,
  SignOutIcon,
  UserIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import useGetRequestsCount from "@/app/[lng]/documents/_hooks/use-get-requests-count";
import getOpenProcesses from "@/app/[lng]/processes/_components/open-processes-tab-content/get-open-processes";
import { LanguageSwitcherMenuItem } from "@/components/common/language-switcher";
import { LocalizedLink } from "@/components/common/localized-link";
import Sparkle from "@/components/features/center-stage/cv-enhance/utils/sparkle";
import { SignOutDialog } from "@/components/layout/sidebar/sign-out-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { usePlan } from "@/contexts/plan-context";
import { useActivePath } from "@/hooks/use-active-path";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import { usePaidFeatureExclusion } from "@/hooks/use-paid-feature-exclusion";
import useReports from "@/hooks/use-reports";
import { useTranslation } from "@/i18n/client";
import EvaluarLogo from "@/public/images/logos/evaluar.svg";
import EvaluarIsotype from "@/public/images/logos/evaluar-isotype.svg";
import ProcessRepository from "@/repositories/process/process-repository";
import useBoundStore from "@/store/bound-store";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { getSignOutRedirectUri, isAuthenticated } from "@/utils/auth";
import { hasActivePlan } from "@/utils/plan-utils";
import { useProductionDebug } from "@/utils/production-debug";
import { cn } from "@/utils/shared";
import { getInitials } from "@/utils/string-utils";
import { clearSignoutTransitionState } from "@/utils/tab-session";
// import package version
import packageJson from "../../../package.json";
import SidebarPlanMenu from "./components/sidebar-plan-menu/sidebar-plan-menu";
import SidebarSkeleton from "./components/sidebar-skeleton/sidebar-skeleton";

const NotificationsInbox = dynamic(
  () => import("./components/notifications-inbox/notifications-inbox"),
  { ssr: false }
);

import { PlanType } from "@/repositories/plan/types";
import SupportMenuItem from "./components/support-menu-item/support-menu-item";

interface RouteItem {
  href: string;
  name: string;
  label: string;
  icon: React.ComponentType<IconProps>;
  badge?: string;
  className?: string;
  requiresB2CAccess?: boolean;
}

interface SidebarDropdownMenuItem {
  path: string;
  label: string;
  icon?: React.ComponentType<IconProps>;
  imageSrc?: string;
  imageAlt?: string;
}

function CvEnhanceSidebarIcon(props: IconProps) {
  const { size, className } = props;
  const iconSize = typeof size === "number" ? size : 16;
  return <Sparkle size={iconSize} className={className} />;
}

interface SidebarProps {
  isMobileSheet?: boolean;
}

export default function LayoutSidebar({ isMobileSheet }: SidebarProps) {
  useProductionDebug("Sidebar");
  const processRepository = new ProcessRepository();
  const { t: tSidebar } = useTranslation("sidebar");
  const { t: tCommon } = useTranslation("common");
  const { t: tPlan } = useTranslation("cv-download");
  const [isAuthenticatedUser, setIsAuthenticatedUser] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Get sidebar state from Zustand
  const sidebarLayoutState = useBoundStore.use.sidebarLayoutState();

  // Always call the hook unconditionally (React rules of hooks)
  const sidebarHookValue = useSidebar();

  // Determine if sidebar should be collapsed based on layout state
  // In mobile sheet, always show expanded regardless of Zustand state
  const shouldCollapseSidebar = isMobileSheet ? false : sidebarLayoutState !== "main";

  // Only use sidebar context in desktop mode
  const sidebarContext = isMobileSheet
    ? {
        state: "expanded" as const,
        setOpen: () => {
          // Mobile sidebar state is controlled by sheet
        },
      }
    : sidebarHookValue;
  const { setOpen } = sidebarContext;

  const personData = useBoundStore.use.person();
  const { isActive } = useActivePath();
  const { isPaidFeatureExcluded } = usePaidFeatureExclusion();
  const environment = usePersistedBoundStore.use.environment();
  const { plan, isLoading } = usePlan();
  const isPlanActive = hasActivePlan(plan);

  const firstName = get(personData, "firstName", null);
  const lastName = get(personData, "lastName", null);
  const urlAvatar = get(personData, "urlAvatar", null);
  const identification = get(personData, "identification", null);
  // const mail = get(personData, "mail", null);

  const currentUserManager = useCurrentUserManager();

  const isControlledProcess = get(environment, "environmentType.Name") === "CONTROLLED";
  const isProfileComplete = !!get(personData, "completeProfile", false);

  // controlled basic & standard
  const isControlledBasic = isControlledProcess && !isProfileComplete;
  const isControlledStandard = isControlledProcess && isProfileComplete;

  const hasEligiblePlan = [PlanType.PASSIVE_RESEARCH, PlanType.ACTIVE_RESEARCH].includes(
    plan?.planType as PlanType
  );

  const shouldShowNotifications =
    !isLoading &&
    !isPaidFeatureExcluded &&
    !isControlledBasic &&
    Boolean(personData?.id) &&
    Boolean(process.env.NEXT_PUBLIC_NOVU_APPLICATION_ID) &&
    hasEligiblePlan;

  const { data: openProcesses } = useQuery({
    queryKey: ["processes", "", "OPEN"],
    queryFn: () => processRepository.getProcesses("", "OPEN"),
    enabled: isAuthenticatedUser && !isControlledProcess && isProfileComplete,
    refetchOnMount: true,
  });
  const { reportsAcquiredCount, reportsAvailableCount } = useReports();
  const { data: requestsCount } = useGetRequestsCount();

  // Get the number of open processes after filtering
  const processes = get(openProcesses, "data", []);
  const actuallyOpenProcesses = getOpenProcesses(processes);
  const openProcessCount = actuallyOpenProcesses.length;
  const documentsRequestCount = get(requestsCount, "data.count", 0);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      clearSignoutTransitionState();
      const redirectUri = getSignOutRedirectUri(environment);
      await currentUserManager?.signoutRedirect(
        redirectUri ? { post_logout_redirect_uri: redirectUri } : undefined
      );
    } catch (_error) {
      // Sign out errors are handled by finally block
    } finally {
      setIsSigningOut(false);
    }
  };

  const openSignOutDialog = () => {
    setIsSignOutDialogOpen(true);
  };

  const closeSignOutDialog = () => {
    setIsSignOutDialogOpen(false);
  };

  // Handle "Mi CV" button click - toggle submenu when already in /profile
  const handleProfileClick = (e: React.MouseEvent) => {
    const profilePaths = ["/settings/personal", "/settings/professional", "/settings/additional"];
    const isInProfileRoute = profilePaths.some(isActive);

    if (isInProfileRoute) {
      // If already in /profile route, toggle the submenu
      e.preventDefault();
      const { sidebarLayoutState, setSidebarLayoutState, resetSidebarLayoutState } =
        useBoundStore.getState();

      if (sidebarLayoutState === "profile") {
        resetSidebarLayoutState();
      } else {
        setSidebarLayoutState("profile");
      }
    }
    // If not in /profile, let the link navigate normally
  };

  const commonRoutes: RouteItem[] = isControlledBasic
    ? []
    : isControlledStandard
      ? [
          {
            href: "/settings/personal",
            name: "profile",
            label: tSidebar("myCV"),
            icon: UserIcon,
          },
          {
            href: "/",
            name: "process",
            label: tSidebar("myProcesses"),
            icon: NotePencilIcon,
            badge: openProcessCount.toString(),
          },
        ]
      : [
          {
            href: "/",
            name: "home",
            label: tSidebar("home"),
            icon: HouseIcon,
            requiresB2CAccess: true,
          },
          {
            href: "/settings/personal",
            name: "profile",
            label: tSidebar("myCV"),
            icon: UserIcon,
          },
          {
            href: "/processes",
            name: "process",
            label: tSidebar("myProcesses"),
            icon: NotePencilIcon,
            badge: openProcessCount.toString(),
          },
          {
            href: "/documents",
            name: "documents",
            label: tSidebar("documents"),
            icon: PaperclipIcon,
            badge: documentsRequestCount.toString(),
          },
        ];

  const calculatedFullName =
    [firstName, lastName].filter(Boolean).join(" ").toLowerCase() || identification;

  const displayName = useMemo(() => {
    const nameParts = firstName?.split(" ").slice(0, 2) || [];
    return nameParts.join(" ").toLowerCase() || identification;
  }, [firstName, identification]);

  const visibleCommonRoutes = commonRoutes;

  const dropdownMenuProfile: SidebarDropdownMenuItem[] = isControlledBasic
    ? []
    : [
        {
          path: "/settings",
          label: tSidebar("accountSettings"),
          icon: GearIcon,
        },
        {
          path: "/settings/billing",
          label: tSidebar("subscription"),
          icon: ShootingStarIcon,
        },
        {
          path: "/plans",
          label: tSidebar("enhance-plan"),
          imageSrc: "/images/shapes/crown-simple.svg",
          imageAlt: "Crown icon",
        },
      ];

  const visibleDropdownMenuProfile = dropdownMenuProfile.filter((item) => {
    if (!isPaidFeatureExcluded) {
      return true;
    }

    return item.path !== "/settings/billing" && item.path !== "/plans";
  });

  const visibleEvaluarCareerRoutes = useMemo(() => {
    const evaluarCareerRoutes: RouteItem[] = isControlledBasic
      ? []
      : isControlledStandard
        ? [
            {
              href: "/reports",
              name: "reports",
              label: tSidebar("reports"),
              icon: FileTextIcon,
              badge: (reportsAcquiredCount + reportsAvailableCount).toString(),
            },
            {
              href: "/enhance",
              name: "enhance",
              label: tSidebar("enhanceProfile"),
              icon: RocketLaunchIcon,
              requiresB2CAccess: true,
            },
          ]
        : [
            {
              href: "/cv-enhance",
              name: "cv-enhance",
              label: tSidebar("cvEnhance"),
              icon: CvEnhanceSidebarIcon,
              requiresB2CAccess: true,
            },
            {
              href: "/cover-letter",
              name: "cover-letter",
              label: tSidebar("coverLetter"),
              icon: EnvelopeIcon,
              requiresB2CAccess: true,
            },
            {
              href: "/interviewer",
              name: "interviewer",
              label: tSidebar("interviewer"),
              icon: ChatsCircleIcon,
              requiresB2CAccess: true,
            },
            {
              href: "/reports",
              name: "reports",
              label: tSidebar("reports"),
              icon: FileTextIcon,
              badge: (reportsAcquiredCount + reportsAvailableCount).toString(),
            },
            {
              href: "/jobs",
              name: "jobs",
              label: tSidebar("evaluarJobs"),
              icon: UsersThreeIcon,
              requiresB2CAccess: true,
            },
            {
              href: "/enhance",
              name: "enhance",
              label: tSidebar("enhanceProfile"),
              icon: RocketLaunchIcon,
              requiresB2CAccess: true,
            },
          ];

    if (isPaidFeatureExcluded) {
      return evaluarCareerRoutes.filter((route) => route.requiresB2CAccess !== true);
    }

    return evaluarCareerRoutes;
  }, [
    reportsAcquiredCount,
    reportsAvailableCount,
    tSidebar,
    isControlledBasic,
    isControlledStandard,
    isPaidFeatureExcluded,
  ]);

  useEffect(() => {
    const checkAuth = async () => {
      const authStatus = await isAuthenticated(currentUserManager);
      setIsAuthenticatedUser(authStatus);
    };
    checkAuth();
  }, [currentUserManager]);

  // Collapse sidebar when there's a secondary sidebar active
  useEffect(() => {
    if (shouldCollapseSidebar && !isMobileSheet) {
      setOpen(false);
    } else if (!shouldCollapseSidebar && !isMobileSheet) {
      setOpen(true);
    }
  }, [shouldCollapseSidebar, isMobileSheet, setOpen]);

  return (
    <>
      <Sidebar
        collapsible={isMobileSheet ? "none" : "icon"}
        className={cn(
          "bg-neutral-100",
          isMobileSheet
            ? "h-full w-[240px] rounded-2xl"
            : shouldCollapseSidebar
              ? "h-screen w-[60px] border-r border-r-primary-200"
              : "h-screen w-[240px] border-r border-r-primary-200"
        )}
      >
        <SidebarHeader
          className={`border-primary-200 ${shouldCollapseSidebar ? "p-4" : "py-4 px-6"}`}
        >
          <div className="flex items-center justify-between w-full">
            <LocalizedLink
              href="/"
              className={cn("flex items-center", shouldCollapseSidebar ? "justify-center" : "")}
            >
              <Image
                src={shouldCollapseSidebar ? EvaluarIsotype : EvaluarLogo}
                alt={tCommon("evaluarLogo")}
                width={shouldCollapseSidebar ? 28 : 98}
                priority={true}
                className="animate-in slide-in-from-left"
              />
            </LocalizedLink>
          </div>
        </SidebarHeader>
        <Separator orientation="horizontal" className="bg-primary-200" />
        <SidebarContent className="gap-0 overflow-y-auto flex flex-col">
          {shouldShowNotifications && (
            <>
              <NotificationsInbox shouldCollapseSidebar={shouldCollapseSidebar} />
              <Separator orientation="horizontal" className="bg-primary-200" />
            </>
          )}
          <SidebarGroup className="m-0 p-0">
            <SidebarGroupContent className="m-0 p-0">
              <SidebarMenu className="gap-2 p-3">
                {(isLoading || !plan) && !isControlledBasic ? (
                  <SidebarSkeleton count={3} />
                ) : (
                  visibleCommonRoutes.map((route) => {
                    const IconComponent = route.icon;
                    const active = isActive(route.href);
                    const isProfileRoute = route.name === "profile";

                    return (
                      <TooltipProvider key={route.name} delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild={true}>
                            <SidebarMenuItem className="flex justify-center" key={route.name}>
                              <SidebarMenuButton
                                asChild={true}
                                isActive={active}
                                className={cn(
                                  " text-sm transition-colors animate-in slide-in-from-left",
                                  active
                                    ? "text-primary-900 bg-primary-200 rounded-[8px] hover:bg-primary-200 hover:text-primary-900"
                                    : "text-neutral-700 hover:text-primary-600 hover:bg-transparent"
                                )}
                              >
                                <LocalizedLink
                                  href={route.href}
                                  className="flex items-center"
                                  onClick={isProfileRoute ? handleProfileClick : undefined}
                                >
                                  <IconComponent
                                    size={18}
                                    weight="duotone"
                                    className="flex-shrink-0 text-inherit"
                                  />
                                  <Typography
                                    variant="label 4"
                                    className={cn(
                                      "text-inherit",
                                      shouldCollapseSidebar ? "hidden" : ""
                                    )}
                                  >
                                    {route.label}
                                  </Typography>
                                  {route.badge && route.badge !== "0" && (
                                    <SidebarMenuBadge className="flex static ml-auto bg-primary-100 rounded-lg">
                                      <Typography variant="label 6" className="text-primary-600">
                                        {route.badge}
                                      </Typography>
                                    </SidebarMenuBadge>
                                  )}
                                </LocalizedLink>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            className={cn(
                              "bg-neutral-100 border border-primary-200 text-neutral-700 z-200",
                              shouldCollapseSidebar ? "" : "hidden"
                            )}
                          >
                            <Typography variant="body 2">{route.label}</Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          {!isControlledBasic && (
            <>
              <Separator orientation="horizontal" className="bg-primary-200" />
              <SidebarGroup className="py-4 px-3">
                {!isPaidFeatureExcluded && (
                  <div
                    className={`${shouldCollapseSidebar ? "hidden" : "flex"} items-center gap-1 mb-2`}
                  >
                    {isLoading || !plan ? (
                      <div className="flex items-center gap-1">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-4 w-4 rounded-md flex-shrink-0" />
                      </div>
                    ) : (
                      <>
                        <Typography variant="label 6" className="text-neutral-600">
                          Evaluar Premium
                        </Typography>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild={true}>
                              <InfoIcon size={16} className="text-primary-600" />
                            </TooltipTrigger>
                            <TooltipContent
                              side="bottom"
                              align="end"
                              className="flex flex-col w-[240px] rounded-lg p-3 gap-1 bg-neutral-50 border border-primary-200 shadow-sm shadow-primary-600/20 ml-3"
                            >
                              <TooltipArrow className="fill-primary-200 w-[20px] h-[4px]" />
                              <Typography
                                variant="label 6"
                                className="font-semibold text-primary-600"
                              >
                                <Trans
                                  t={tSidebar}
                                  i18nKey="evaluar-premium.title"
                                  components={{ strong: <strong className="font-bold" /> }}
                                />
                              </Typography>
                              <ul className="list-disc pl-5 space-y-1">
                                <li className="text-neutral-700 pl-1">
                                  <Typography variant="label 6" className="text-neutral-700 ">
                                    {tSidebar("evaluar-premium.item-1")}
                                  </Typography>
                                </li>
                                <li className="text-neutral-700 pl-1">
                                  <Typography variant="label 6" className="text-neutral-700">
                                    {tSidebar("evaluar-premium.item-2")}
                                  </Typography>
                                </li>
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </>
                    )}
                  </div>
                )}
                <SidebarGroupContent>
                  <SidebarMenu className="gap-2">
                    {(isLoading || !plan) && !isControlledBasic ? (
                      <SidebarSkeleton count={5} />
                    ) : (
                      visibleEvaluarCareerRoutes.map((route) => {
                        const IconComponent = route.icon;
                        const active = isActive(route.href);

                        return (
                          <TooltipProvider key={route.name} delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild={true}>
                                <SidebarMenuItem className="flex justify-center" key={route.name}>
                                  <SidebarMenuButton
                                    asChild={true}
                                    isActive={active}
                                    className={cn(
                                      "text-sm transition-colors animate-in slide-in-from-left",
                                      active
                                        ? "text-primary-900 bg-primary-200 rounded-[8px] hover:bg-primary-200 hover:text-primary-900"
                                        : "text-neutral-700 hover:text-primary-600 hover:bg-transparent"
                                    )}
                                  >
                                    <LocalizedLink href={route.href} className=" flex items-center">
                                      <IconComponent
                                        size={18}
                                        weight="duotone"
                                        className="flex-shrink-0 text-inherit"
                                      />
                                      <Typography
                                        variant="label 4"
                                        className={cn(
                                          "text-inherit",
                                          shouldCollapseSidebar ? "hidden" : ""
                                        )}
                                      >
                                        {route.label}
                                      </Typography>
                                      {route.badge && route.badge !== "0" && (
                                        <SidebarMenuBadge className="flex static ml-auto bg-primary-100 rounded-lg">
                                          <Typography
                                            variant="label 6"
                                            className="text-primary-600"
                                          >
                                            {route.badge}
                                          </Typography>
                                        </SidebarMenuBadge>
                                      )}
                                    </LocalizedLink>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>
                              </TooltipTrigger>
                              <TooltipContent
                                side="right"
                                className={cn(
                                  "bg-neutral-100 border border-primary-200 text-neutral-700 z-200",
                                  shouldCollapseSidebar ? "" : "hidden"
                                )}
                              >
                                <Typography variant="body 2">{route.label}</Typography>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}

          <div className="flex-1" />
          {!isPaidFeatureExcluded && !isControlledBasic && !isLoading && plan && (
            <div className="w-full px-3 py-3">
              {(!plan || plan?.planType === "FREE") && !shouldCollapseSidebar ? (
                <div className="animate-in slide-in-from-left">
                  <SidebarPlanMenu plan={plan} isMobile={isMobileSheet} />
                </div>
              ) : (!plan || plan?.planType === "FREE") && shouldCollapseSidebar ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild={true}>
                    <Button
                      isJustIcon={true}
                      type="button"
                      loading={isLoading}
                      style={{
                        background: "linear-gradient(90deg, #A092E1 0%, #EC68A3 50%, #FFBB57 100%)",
                      }}
                      className="animate-in slide-in-from-left w-full h-[36px] flex justify-center"
                    >
                      <RocketLaunchIcon
                        size={20}
                        weight="fill"
                        className="text-white flex-shrink-0"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-auto border-none rounded-xl bg-transparent p-0 ms-3 z-200">
                    <SidebarPlanMenu plan={plan} />
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild={true}>
                    <div className="relative w-full animate-in slide-in-from-left cursor-pointer">
                      {!isPlanActive && !shouldCollapseSidebar && (
                        <Badge className="absolute -top-3 left-3 z-10 bg-primary-500 text-white border-none pointer-events-none rounded-[4px]">
                          <Typography variant="label 6">{tSidebar("expired")}</Typography>
                        </Badge>
                      )}
                      <Button
                        isIconPlusText={!shouldCollapseSidebar}
                        isJustIcon={shouldCollapseSidebar}
                        type="button"
                        loading={isLoading}
                        style={{
                          background:
                            "linear-gradient(90deg, #A092E1 0%, #EC68A3 50%, #FFBB57 100%)",
                        }}
                        className="w-full h-[36px] flex justify-between"
                      >
                        <div className="flex gap-2 items-center border-white">
                          <CrownSimpleIcon
                            size={20}
                            weight="fill"
                            className="text-white flex-shrink-0"
                          />
                          {!shouldCollapseSidebar && (
                            <span className="text-sm font-semibold text-white">
                              {tPlan(`plan-types.${plan?.planType || "FREE"}`)}
                            </span>
                          )}
                        </div>
                        <CaretUpDownIcon size={16} className="text-neutral-0" />
                      </Button>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className={`w-auto border-none rounded-xl bg-transparent p-0 ${
                      shouldCollapseSidebar && "ms-3"
                    } z-200`}
                  >
                    <SidebarPlanMenu plan={plan} />
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </SidebarContent>
        <SidebarFooter className="p-0 m-0 sticky bottom-0 bg-neutral-100 border-t border-primary-200 pb-[env(safe-area-inset-bottom)]">
          <div className="space-y-3">
            <div className="hidden md:block">
              <DropdownMenu open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen} modal={false}>
                <DropdownMenuTrigger asChild={true}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="lg"
                    className="flex justify-between items-center gap-3 w-full rounded-none px-3 py-2 hover:bg-primary-200 transition-colors focus:outline-none"
                  >
                    <Avatar className="h-[24px] w-[24px] mx-auto">
                      <AvatarImage src={urlAvatar || undefined} />
                      <AvatarFallback className="bg-primary-300 text-primary-900">
                        {getInitials(identification || firstName, lastName)}
                      </AvatarFallback>
                    </Avatar>
                    {!shouldCollapseSidebar && (
                      <>
                        <div className="flex flex-col items-start flex-1 max-w-[140px]">
                          <Typography
                            variant="label 4"
                            className="text-neutral-900 capitalize w-full overflow-hidden text-left text-ellipsis"
                          >
                            {calculatedFullName}
                          </Typography>
                        </div>
                        <CaretUpDownIcon size={16} className="text-neutral-600 flex-shrink-0" />
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  className="z-200 flex w-[216px] flex-col gap-0 rounded-lg border border-primary-200 bg-neutral-50 p-3 shadow-[0px_-2px_13px_rgba(105,95,151,0.10)]"
                >
                  <DropdownMenuItem className="h-9 w-full gap-2 rounded-lg p-0 focus:bg-transparent">
                    <Typography
                      variant="caption 2"
                      className="text-neutral-300 absolute -top-1 -right-1"
                    >
                      v{packageJson.version}
                    </Typography>
                    <Avatar className="h-[32px] w-[32px]">
                      <AvatarImage src={urlAvatar || undefined} />
                      <AvatarFallback className="bg-primary-300 text-primary-900">
                        {getInitials(firstName || identification, lastName || identification)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild={true}>
                            <Typography
                              variant="label 6"
                              className="text-neutral-900 font-semibold capitalize truncate w-full text-left cursor-default"
                            >
                              {displayName}
                            </Typography>
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            className="bg-neutral-100 border border-primary-200 text-neutral-700 z-[250] max-w-[300px] px-2 py-1"
                          >
                            <Typography variant="label 6" className="capitalize break-words">
                              {displayName}
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild={true}>
                            <Typography
                              variant="label 6"
                              className="text-neutral-600 truncate w-full text-left cursor-default"
                            >
                              {personData?.mail}
                            </Typography>
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            className="bg-neutral-100 border border-primary-200 text-neutral-700 z-[250] max-w-[300px] px-2 py-1"
                          >
                            <Typography variant="label 6" className="break-words">
                              {personData?.mail}
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </DropdownMenuItem>

                  <Separator orientation="horizontal" className="bg-primary-200 my-2" />
                  <div className="flex flex-col gap-2">
                    {visibleDropdownMenuProfile.map((item, index) => {
                      const IconComponent = item.icon;
                      return (
                        <DropdownMenuItem
                          key={index}
                          className="h-6 w-full cursor-pointer rounded-lg px-1 py-1 focus:bg-primary-100 hover:bg-primary-100"
                          asChild={true}
                        >
                          <LocalizedLink href={item.path} className="flex gap-2 items-center">
                            {IconComponent ? (
                              <IconComponent
                                size={16}
                                weight="duotone"
                                className="size-4 text-neutral-700"
                              />
                            ) : item.imageSrc ? (
                              <Image
                                src={item.imageSrc}
                                alt={item.imageAlt || ""}
                                width={16}
                                height={16}
                                className="flex-shrink-0"
                              />
                            ) : null}
                            <Typography
                              variant="label 6"
                              className="flex items-center text-neutral-700"
                            >
                              {item.label}
                            </Typography>
                          </LocalizedLink>
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuItem
                      className="h-6 w-full cursor-pointer rounded-lg px-1 py-1 focus:bg-primary-100 hover:bg-primary-100"
                      asChild={true}
                    >
                      <LocalizedLink
                        href="/faq"
                        target="_blank"
                        className="flex gap-2 items-center"
                      >
                        <QuestionIcon
                          size={16}
                          weight="duotone"
                          className="size-4 text-neutral-700"
                        />
                        <Typography
                          variant="label 6"
                          className="flex items-center text-neutral-700"
                        >
                          {tSidebar("faq")}
                        </Typography>
                      </LocalizedLink>
                    </DropdownMenuItem>
                    <SupportMenuItem />
                    <LanguageSwitcherMenuItem
                      onLanguageChange={() => {
                        setIsUserMenuOpen(false);
                      }}
                    />
                  </div>
                  {visibleDropdownMenuProfile.length > 0 && (
                    <Separator orientation="horizontal" className="bg-primary-200 my-2" />
                  )}
                  <DropdownMenuItem
                    className="h-6 w-full cursor-pointer rounded-lg px-1 py-1 focus:bg-error-100 hover:bg-error-100"
                    onClick={openSignOutDialog}
                    disabled={isSigningOut}
                  >
                    <div className="flex gap-2 items-center">
                      <SignOutIcon size={16} className="size-4 text-error-400" />
                      <Typography variant="label 6" className="flex items-center text-neutral-700">
                        {isSigningOut ? `${tSidebar("signingOut")}...` : tSidebar("logout")}
                      </Typography>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </SidebarFooter>
      </Sidebar>

      {/* SignOutDialog */}
      <SignOutDialog
        isOpen={isSignOutDialogOpen}
        onClose={closeSignOutDialog}
        onConfirm={handleSignOut}
        isSigningOut={isSigningOut}
      />
    </>
  );
}
